'use client'

import { useEffect, useRef } from 'react'
import { onAppPause, onAppResume } from '@/lib/utils/deep-linking'

/** `visibilitychange` and Capacitor's `resume` both fire for one return; count it once. */
const DEDUPE_MS = 1_000

/**
 * Run `callback` when the user comes back to the app after being away.
 *
 * A phone backgrounds the WebView: iOS suspends its websockets and `window` `focus` is
 * unreliable on return, so counters and feeds were left stale until the app was killed.
 * Listens to `visibilitychange` (web and WebView) and Capacitor's `pause`/`resume` (native).
 *
 * A return only counts when a departure was recorded first, and the departure is consumed by
 * whichever signal arrives first: the twin signal of the same return (and the `resume` that
 * follows a permission dialog or a file chooser, which has a `pause` of a few seconds) can
 * not slip past `minAwayMs`. `minAwayMs` ignores quick app switches (default 0: any return).
 */
export function useOnResume(callback: () => void, { minAwayMs = 0 }: { minAwayMs?: number } = {}) {
  const callbackRef = useRef(callback)
  useEffect(() => {
    callbackRef.current = callback
  })

  useEffect(() => {
    let leftAt: number | null = document.visibilityState === 'hidden' ? Date.now() : null
    let lastFiredAt = 0

    const left = () => {
      if (leftAt === null) leftAt = Date.now()
    }

    const returned = () => {
      if (leftAt === null) return
      const now = Date.now()
      const awayFor = now - leftAt
      leftAt = null
      if (awayFor < minAwayMs || now - lastFiredAt < DEDUPE_MS) return
      lastFiredAt = now
      callbackRef.current()
    }

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') left()
      else returned()
    }
    document.addEventListener('visibilitychange', onVisibility)

    let removePause: (() => void) | undefined
    let removeResume: (() => void) | undefined
    let cancelled = false
    void onAppPause(left).then((remove) => {
      if (cancelled) remove()
      else removePause = remove
    })
    void onAppResume(returned).then((remove) => {
      if (cancelled) remove()
      else removeResume = remove
    })

    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisibility)
      removePause?.()
      removeResume?.()
    }
  }, [minAwayMs])
}
